import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ButtonDecorator } from '../../../components/ButtonDecorator/ButtonDecorator';
import { Bem } from '../../../service/Bem/Bem';
import { ValidationErrorData } from '../../../typings/common';
import { PcodeRenderDataCanvas } from '../../../typings/pcode';
import { Page } from '../../common/typings';
import {
    CommonCreativesSelector,
    CommonCreativesSelectorProps
} from '../../CommonCreativesSelector/CommonCreativesSelector';
import { CreativeOptionsData } from '../../CreativeOptions/typings';
import { CreativeSmartCenterModalContainer } from '../../CreativeSmartCenterModal/CreativeSmartCenterModalContainer';
import { PcodeCanvasPreview } from '../../PcodeCanvasPreview/PcodeCanvasPreview';
import { AddCreativePage } from '../AddCreativePage';
import { GraphicPreview } from '../GraphicPreview/GraphicPreview';
import { CommonCreativeBaseItem } from '../typings';

import './AddAdaptiveCreative.css';
import * as keyset from './AddAdaptiveCreative.i18n';

const i18n = i18nFactory(keyset);

export interface AddAdaptiveCreativeProps {
    isLoading: boolean;
    isSaving: boolean;

    creativeOptions?: CreativeOptionsData;
    onSaveName: (name: string) => void;
    onOptionChange: (groupName: string, name: string, value: string) => void;
    onGroupVisibleChange: (groupName: string) => void;

    returnPage?: Page;
    onReturnClick: (page?: Page) => void;
    error?: ValidationErrorData;
    onSave: () => void;
    onCancel: () => void;

    previewsModalVisible?: boolean;
    createPressed?: boolean;
    previewItems?: CommonCreativeBaseItem[];
    recropEnabled: boolean;
    smartCenterClose: () => void;

    showPreviewsModal: () => void;
    cancelPreviewsModal: () => void;
    closePreviewsModal: () => void;
    submitPreviewsModal: (continueEdit?: boolean) => void;

    item?: CommonCreativeBaseItem;
    pcodeData?: PcodeRenderDataCanvas;

    init: () => void;
}

const BLOCK = 'add-adaptive-creative';

const renderBottomPane = (showAllPreviews: () => void): React.ReactNode => (
    <div className={Bem.cls(BLOCK, 'bottom-pane')}>
        <ButtonDecorator
            cls={Bem.cls(BLOCK, 'show-all-button')}
            theme="normal"
            size="xs"
            text={i18n('show-all-previews')}
            onClick={showAllPreviews}
        />
    </div>
);

const getPreviewModalProps = (
    continueEdit: () => void,
    cancelEnabled: boolean,
    createPressed?: boolean
): Pick<
    CommonCreativesSelectorProps,
    'submitText' | 'cancelText' | 'cancelEnabled' | 'additionalCancel' | 'additionalCancelText'
> => {
    if (createPressed) {
        return {
            submitText: i18n('ok'),
            cancelText: i18n('reset-smart-center'),
            cancelEnabled,
            additionalCancel: continueEdit,
            additionalCancelText: i18n('continue-edit')
        };
    }

    return {
        submitText: i18n('ok'),
        cancelText: i18n('reset-smart-center'),
        cancelEnabled
    };
};

export const AddAdaptiveCreative = React.memo((props: AddAdaptiveCreativeProps) => {
    const {
        item,
        pcodeData,
        showPreviewsModal,
        submitPreviewsModal,
        cancelPreviewsModal,
        closePreviewsModal,
        previewsModalVisible,
        recropEnabled,
        createPressed,
        previewItems,
        init,
        smartCenterClose,
        ...addCreativeProps
    } = props;

    useEffect(() => init(), []);

    const previewRef = useRef<PcodeCanvasPreview>(null);
    const preview = useMemo(
        () => (
            <GraphicPreview
                item={item}
                pcodeData={pcodeData}
                ref={previewRef}
                cls={Bem.cls(BLOCK, 'preview-container')}
            />
        ),
        [pcodeData, item]
    );
    const bottomPane = useMemo(() => renderBottomPane(showPreviewsModal), []);
    const continueEdit = useCallback(() => submitPreviewsModal(createPressed), [createPressed]);

    return (
        <>
            <AddCreativePage
                {...addCreativeProps}
                isDocCenterTooltip={false}
                preview={preview}
                bottomPane={bottomPane}
                contentCls={Bem.cls(BLOCK, 'content')}
                previewCls={Bem.cls(BLOCK, 'preview')}
            />
            {previewsModalVisible && previewItems && (
                <CommonCreativesSelector
                    cls={Bem.cls(BLOCK, 'previews-modal')}
                    header={i18n('previews-title')}
                    items={previewItems}
                    submit={submitPreviewsModal}
                    cancel={cancelPreviewsModal}
                    close={closePreviewsModal}
                    link={false}
                    sizeFilter={false}
                    previewControls={false}
                    showId={false}
                    {...getPreviewModalProps(continueEdit, recropEnabled, createPressed)}
                />
            )}

            <CreativeSmartCenterModalContainer onClose={smartCenterClose} />
        </>
    );
});
